import { useEffect, useState } from 'react';
import type { ProgramDirectorDashboard } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';

export function PDDashboardPage() {
  const [data, setData] = useState<ProgramDirectorDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .pdDashboard()
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading) return <LoadingState message="Loading program overview…" />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!data) return null;

  const stats = [
    { label: 'Active cohorts', value: data.counts.activeCohorts },
    { label: 'Students', value: data.counts.students },
    { label: 'Instructors', value: data.counts.instructors },
    { label: 'Clinical sites', value: data.counts.activeClinicalSites },
    { label: 'Specialties', value: data.counts.activeSpecialties },
    { label: 'Procedures', value: data.counts.procedures },
    { label: 'Requirement plans', value: data.counts.requirementPlans },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Program Dashboard"
        subtitle={`${data.programName} · ${data.organizationName}`}
      />

      <div className="stat-grid stat-grid--wide">
        {stats.map((s) => (
          <div key={s.label} className="card stat-card">
            <span className="stat-card__label">{s.label}</span>
            <span className="stat-card__value">{s.value}</span>
          </div>
        ))}
      </div>

      {data.recentAuditEvents.length > 0 && (
        <section className="card">
          <h2 className="card__title">Recent activity</h2>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Target</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {data.recentAuditEvents.map((e) => (
                  <tr key={e.id}>
                    <td>{e.eventType.replace(/_/g, ' ')}</td>
                    <td>{e.targetType}</td>
                    <td>{new Date(e.occurredAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
